import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { X, Plus, Star } from "lucide-react";

const LANGS = ["PT", "EN", "ES", "FR", "DE", "IT", "ZH"];
const DAYS_LABELS = { PT: ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"], EN: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] };

export default function DriverProfile() {
  const [d, setD] = useState(null);
  const { t, lang } = useLang();
  const load = () => api.get("/driver/me").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-5 space-y-6">
      <div>
        <p className="label-caps mb-1">{t("profile")}</p>
        <h1 className="font-display text-3xl text-[#0a0f1e]">{d.name}</h1>
        <div className="flex items-center gap-1 mt-2">
          <Star className="w-4 h-4 text-[#f59e0b] fill-[#f59e0b]" />
          <span className="text-[#0a0f1e]">{d.rating_avg?.toFixed(1) || "—"}</span>
          <span className="text-xs text-slate-500 ml-1">({d.trips_count} trips)</span>
        </div>
      </div>

      <Tabs defaultValue="me">
        <TabsList className="w-full bg-transparent border border-slate-200 rounded-none h-auto p-0 grid grid-cols-5">
          <TabsTrigger value="me" className="text-[10px] rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">Me</TabsTrigger>
          <TabsTrigger value="avail" data-testid="tab-availability" className="text-[10px] rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">{t("availability")}</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers" className="text-[10px] rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">Ofertas</TabsTrigger>
          <TabsTrigger value="price" data-testid="tab-pricing" className="text-[10px] rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">{t("pricing")}</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs" className="text-[10px] rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">{t("documents")}</TabsTrigger>
        </TabsList>

        <TabsContent value="me" className="mt-4"><MeTab d={d} onSaved={load} /></TabsContent>
        <TabsContent value="avail" className="mt-4"><AvailTab d={d} onSaved={load} lang={lang} /></TabsContent>
        <TabsContent value="offers" className="mt-4"><OffersTab d={d} onSaved={load} /></TabsContent>
        <TabsContent value="price" className="mt-4"><PriceTab d={d} onSaved={load} /></TabsContent>
        <TabsContent value="docs" className="mt-4"><DocsTab d={d} onSaved={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

async function save(payload, onSaved, label = "Saved") {
  try { await api.patch("/driver/profile", payload); toast.success(label); onSaved(); }
  catch (e) { toast.error(formatApiError(e)); }
}

function MeTab({ d, onSaved }) {
  const [f, setF] = useState({ photo_url: d.photo_url || "", languages: d.languages || [], bio_pt: d.bio_pt || "", bio_en: d.bio_en || "" });
  const toggle = (l) => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter(x => x !== l) : [...f.languages, l] });
  return (
    <div className="space-y-4">
      <div>
        <label className="label-caps block mb-2">Photo URL</label>
        <Input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} className="input-dt" data-testid="driver-photo" />
      </div>
      <div>
        <label className="label-caps block mb-2">Languages</label>
        <div className="flex flex-wrap gap-2">
          {LANGS.map((l) => (
            <button key={l} onClick={() => toggle(l)} className={`px-3 py-1.5 text-xs border ${f.languages.includes(l) ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="label-caps block mb-2">Bio (PT)</label>
        <Textarea value={f.bio_pt} onChange={(e) => setF({ ...f, bio_pt: e.target.value })} rows={3} className="input-dt" />
      </div>
      <div>
        <label className="label-caps block mb-2">Bio (EN)</label>
        <Textarea value={f.bio_en} onChange={(e) => setF({ ...f, bio_en: e.target.value })} rows={3} className="input-dt" />
      </div>
      <button data-testid="save-me" onClick={() => save(f, onSaved, "Profile saved")} className="w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide">Save</button>
    </div>
  );
}

function AvailTab({ d, onSaved, lang }) {
  const labels = DAYS_LABELS[lang] || DAYS_LABELS.EN;
  const [wh, setWh] = useState(d.working_hours && d.working_hours.length ? d.working_hours : labels.map((_, i) => ({ day: i, enabled: true, start: "08:00", end: "22:00" })));
  const [blocked, setBlocked] = useState((d.blocked_dates || []).join(", "));
  const upd = (i, key, val) => setWh(wh.map((w, idx) => idx === i ? { ...w, [key]: val } : w));
  return (
    <div className="space-y-3">
      {labels.map((label, i) => {
        const w = wh[i] || { day: i, enabled: false, start: "08:00", end: "22:00" };
        return (
          <div key={i} className="border border-slate-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#0a0f1e]">{label}</span>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={w.enabled} onChange={(e) => upd(i, "enabled", e.target.checked)} className="accent-[#f59e0b]" data-testid={`day-${label}`} />
                <span className="text-xs text-slate-500">On</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="time" value={w.start} onChange={(e) => upd(i, "start", e.target.value)} className="input-dt" disabled={!w.enabled} />
              <Input type="time" value={w.end} onChange={(e) => upd(i, "end", e.target.value)} className="input-dt" disabled={!w.enabled} />
            </div>
          </div>
        );
      })}
      <div>
        <label className="label-caps block mb-2">Blocked dates</label>
        <Input value={blocked} onChange={(e) => setBlocked(e.target.value)} placeholder="2026-03-15, 2026-04-01" className="input-dt" />
      </div>
      <button onClick={() => save({ working_hours: wh, blocked_dates: blocked.split(",").map(s => s.trim()).filter(Boolean) }, onSaved, "Availability saved")} className="w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs">Save</button>
    </div>
  );
}

function PriceTab({ d, onSaved }) {
  const [f, setF] = useState({
    pricing_km: d.pricing_km, pricing_hour: d.pricing_hour,
    night_supplement_pct: d.night_supplement_pct, weekend_supplement_pct: d.weekend_supplement_pct,
    fixed_routes: d.fixed_routes || [],
  });
  const upd = (i, key, val) => setF({ ...f, fixed_routes: f.fixed_routes.map((r, idx) => idx === i ? { ...r, [key]: key === "price" ? Number(val) : val } : r) });
  const add = () => setF({ ...f, fixed_routes: [...f.fixed_routes, { origin: "", destination: "", price: 0 }] });
  const rm = (i) => setF({ ...f, fixed_routes: f.fixed_routes.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-3">
      <F l="Price per km (€)"><Input type="number" step="0.01" value={f.pricing_km} onChange={(e) => setF({ ...f, pricing_km: Number(e.target.value) })} className="input-dt" data-testid="drv-km" /></F>
      <F l="Price per hour (€)"><Input type="number" step="0.01" value={f.pricing_hour} onChange={(e) => setF({ ...f, pricing_hour: Number(e.target.value) })} className="input-dt" data-testid="drv-hour" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F l="Night supp %"><Input type="number" value={f.night_supplement_pct} onChange={(e) => setF({ ...f, night_supplement_pct: Number(e.target.value) })} className="input-dt" /></F>
        <F l="Weekend supp %"><Input type="number" value={f.weekend_supplement_pct} onChange={(e) => setF({ ...f, weekend_supplement_pct: Number(e.target.value) })} className="input-dt" /></F>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label-caps">Fixed routes</span>
          <button onClick={add} className="text-xs text-[#f59e0b] flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
        </div>
        <div className="space-y-2">
          {f.fixed_routes.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_60px_auto] gap-2">
              <Input placeholder="From" value={r.origin} onChange={(e) => upd(i, "origin", e.target.value)} className="input-dt !h-9 text-xs" />
              <Input placeholder="To" value={r.destination} onChange={(e) => upd(i, "destination", e.target.value)} className="input-dt !h-9 text-xs" />
              <Input type="number" value={r.price} onChange={(e) => upd(i, "price", e.target.value)} className="input-dt !h-9 text-xs" />
              <button onClick={() => rm(i)} className="text-slate-500"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <button onClick={() => save(f, onSaved, "Pricing saved")} className="w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs">Save</button>
    </div>
  );
}

function DocsTab({ d, onSaved }) {
  const [docs, setDocs] = useState(d.documents || []);
  const upd = (i, key, val) => setDocs(docs.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const add = () => setDocs([...docs, { document_type: "driving_license", number: "", expiry_date: "" }]);
  const rm = (i) => setDocs(docs.filter((_, idx) => idx !== i));
  const statusOf = (exp) => {
    if (!exp) return { label: "—", color: "text-slate-500" };
    const days = Math.round((new Date(exp) - new Date()) / 86400000);
    if (days < 0) return { label: "EXPIRED", color: "text-red-400" };
    if (days <= 30) return { label: `${days}d`, color: "text-amber-400" };
    return { label: "VALID", color: "text-emerald-400" };
  };
  return (
    <div className="space-y-3">
      {docs.map((doc, i) => {
        const st = statusOf(doc.expiry_date);
        return (
          <div key={i} className="border border-slate-200 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Input value={doc.document_type} onChange={(e) => upd(i, "document_type", e.target.value)} className="input-dt !h-9 text-xs w-2/3" />
              <span className={`text-[10px] uppercase ${st.color}`}>{st.label}</span>
              <button onClick={() => rm(i)} className="text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <Input placeholder="Number" value={doc.number || ""} onChange={(e) => upd(i, "number", e.target.value)} className="input-dt !h-9 text-xs" />
            <Input type="date" value={doc.expiry_date || ""} onChange={(e) => upd(i, "expiry_date", e.target.value)} className="input-dt !h-9 text-xs" />
          </div>
        );
      })}
      <button onClick={add} className="w-full py-3 border border-dashed border-slate-200 text-xs text-slate-500">+ Add document</button>
      <button onClick={() => save({ documents: docs }, onSaved, "Documents saved")} className="w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs">Save</button>
    </div>
  );
}


function OffersTab({ d, onSaved }) {
  const [catalog, setCatalog] = React.useState(null);
  const [zones, setZones] = React.useState(d.zones_covered || []);
  const [enabledTours, setEnabledTours] = React.useState(d.enabled_tours || null);  // null = all
  const [enabledCats, setEnabledCats] = React.useState(d.enabled_categories || null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/driver/offers/catalog").then((r) => setCatalog(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!catalog) return <div className="p-4 text-sm text-slate-500">A carregar catálogo…</div>;

  const toggleZone = (k) => setZones(zones.includes(k) ? zones.filter(x => x !== k) : [...zones, k]);
  const isAllTours = enabledTours === null || enabledTours.length === 0;
  const isAllCats = enabledCats === null || enabledCats.length === 0;
  const toggleTour = (id) => {
    const cur = enabledTours || catalog.tours.map(t => t.id);
    setEnabledTours(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };
  const toggleCat = (k) => {
    const cur = enabledCats || catalog.categories.map(c => c.key);
    setEnabledCats(cur.includes(k) ? cur.filter(x => x !== k) : [...cur, k]);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch("/driver/profile", {
        zones_covered: zones,
        enabled_tours: isAllTours ? [] : enabledTours,
        enabled_categories: isAllCats ? [] : enabledCats,
      });
      toast.success("Preferências guardadas");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="driver-offers-tab">
      <div className="bg-[#fefce8] border border-amber-200 p-3 text-xs text-amber-900">
        Escolha onde quer trabalhar e que tipo de serviços aceita. Estas preferências filtram automaticamente as ofertas que recebe pelo dispatcher DiTours.
      </div>

      <div>
        <p className="label-caps mb-2">Zonas onde quer trabalhar</p>
        {zones.length === 0 && <p className="text-[11px] text-red-600 mb-2">⚠ Sem zonas selecionadas, não receberá viagens.</p>}
        <div className="grid grid-cols-3 gap-2">
          {catalog.zones.map((z) => (
            <button key={z.key} onClick={() => toggleZone(z.key)} data-testid={`driver-zone-${z.key}`}
              className={`px-3 py-2 text-xs uppercase tracking-wide border font-bold text-left ${zones.includes(z.key) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 text-slate-500"}`}>
              {z.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="label-caps">Categorias de transfer habilitadas</p>
          <button onClick={() => setEnabledCats(isAllCats ? [] : null)} className="text-[10px] uppercase text-[#f59e0b] font-bold" data-testid="cat-toggle-all">
            {isAllCats ? "Selecionar específicas" : "Aceitar todas"}
          </button>
        </div>
        {isAllCats ? (
          <p className="text-[11px] text-emerald-700">✓ A aceitar todas as categorias.</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {catalog.categories.map((c) => (
              <button key={c.key} onClick={() => toggleCat(c.key)} data-testid={`driver-cat-${c.key}`}
                className={`px-3 py-2 text-xs uppercase tracking-wide border font-bold text-left ${(enabledCats || []).includes(c.key) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 text-slate-500"}`}>
                {c.label} · {c.capacity_pax} pax
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="label-caps">Tours habilitados</p>
          <button onClick={() => setEnabledTours(isAllTours ? [] : null)} className="text-[10px] uppercase text-[#f59e0b] font-bold" data-testid="tour-toggle-all">
            {isAllTours ? "Selecionar específicos" : "Aceitar todos"}
          </button>
        </div>
        {isAllTours ? (
          <p className="text-[11px] text-emerald-700">✓ A aceitar todos os {catalog.tours.length} tours.</p>
        ) : (
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {catalog.tours.map((t) => (
              <button key={t.id} onClick={() => toggleTour(t.id)} data-testid={`driver-tour-${t.id}`}
                className={`w-full flex items-center gap-3 p-2 text-xs border font-medium text-left ${(enabledTours || []).includes(t.id) ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200"}`}>
                {t.cover_image ? <img src={t.cover_image} alt="" className="w-10 h-10 object-cover" /> : <div className="w-10 h-10 bg-slate-100" />}
                <div className="flex-1">
                  <div className="text-[#0a0f1e] font-semibold">{t.title}</div>
                  <div className="text-[10px] text-slate-500 uppercase">{t.zone} · {t.duration_hours}h</div>
                </div>
                <div className={`w-4 h-4 border-2 ${(enabledTours || []).includes(t.id) ? "border-[#f59e0b] bg-[#f59e0b]" : "border-slate-300"}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      <button onClick={submit} disabled={saving} className="w-full h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide disabled:opacity-50" data-testid="save-offers">
        {saving ? "A guardar…" : "Guardar preferências"}
      </button>
    </div>
  );
}

function F({ l, children }) { return (<div><label className="label-caps block mb-2">{l}</label>{children}</div>); }
